// Alarm and timer sound, synthesised with Web Audio so there is no asset to cache offline.
// iOS keeps audio muted until a user gesture has started an AudioContext, so the shell calls
// `unlockAudio` on the first touch; a wall display gets touched long before any alarm rings.

type Ctor = typeof AudioContext;

let ctx: AudioContext | null = null;

function context(): AudioContext | null {
  if (ctx) return ctx;
  const w = window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
  const C = w.AudioContext || w.webkitAudioContext;
  if (!C) return null;
  try {
    ctx = new C();
  } catch {
    ctx = null;
  }
  return ctx;
}

/** Call from a touch/click handler. Returns true once audio is allowed to play. */
export function unlockAudio(): boolean {
  const c = context();
  if (!c) return false;
  if (c.state === 'suspended') void c.resume();
  // Safari 12 also wants a sound started inside the gesture; a one-sample silent buffer does it.
  const src = c.createBufferSource();
  src.buffer = c.createBuffer(1, 1, 22050);
  src.connect(c.destination);
  src.start(0);
  return c.state === 'running';
}

function beep(c: AudioContext, at: number, freq: number) {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, at);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(0.5, at + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.35);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(at);
  osc.stop(at + 0.4);
}

/** Repeats a soft two-tone chime until the returned function is called. */
export function startChime(): () => void {
  const c = context();
  if (!c) return () => undefined;
  if (c.state === 'suspended') void c.resume();
  const ring = () => {
    const t = c.currentTime + 0.05;
    beep(c, t, 880);
    beep(c, t + 0.45, 660);
  };
  ring();
  const id = setInterval(ring, 1600);
  return () => clearInterval(id);
}
